import { removeByPath } from "../../../utility";
import {
  ARSTActionContextmenuAction,
  ArsTableCellType,
  ArsTableGroup,
  ArsTableState,
} from "../interface";
import { isArsTableSubGroup } from "../typeguards/isArsTableSubGroup";
import { computeUsedGroupingFactorIds } from "./computeUsedGroupingFactorIds";

/**
 * Removes a grouping factor node from the table body and updates row headers when required.
 *
 * @param state - Current ARS table state
 * @param action - Context-menu action describing the grouping factor cell to remove
 * @returns Updated ARS table state
 */
export const arst_removeGroupingFactor = <T>(
  state: ArsTableState<T>,
  action: ARSTActionContextmenuAction<T>,
): ArsTableState<T> => {
  if (
    action.cellValue.cellType !== ArsTableCellType.ColumnGroupCell &&
    action.cellValue.cellType !== ArsTableCellType.RowGroupCell
  ) {
    return state;
  }

  const newBodyData = removeByPath(
    state.bodyData,
    action.cellValue.dataPath,
    action.cellValue.siblingCount - 1,
  );

  if (
    action.cellValue.cellType === ArsTableCellType.RowGroupCell &&
    state.bodyData.rows.length === 1
  ) {
    const pathParts = action.cellValue.dataPath.split("/").filter((part) => part !== "");
    const depth = pathParts.filter((part) => part === "subGroups").length;

    if (depth === 0) {
      newBodyData.rowHeaders = [];
    } else {
      let headerCount = 0;
      let currentGroup: ArsTableGroup<T> = state.bodyData.rows[0];
      for (let d = 0; d < depth; d += 1) {
        headerCount += currentGroup.hidden ? 1 : 2;
        if (d < depth - 1) {
          const subGroupIndex = parseInt(pathParts[2 * (d + 1) + 1], 10);
          /* istanbul ignore else */
          if (isArsTableSubGroup(currentGroup, state.settings.editorProps.isT)) {
            currentGroup = currentGroup.subGroups![subGroupIndex];
          }
        }
      }
      newBodyData.rowHeaders = newBodyData.rowHeaders.slice(0, headerCount);
    }
  }

  return {
    ...state,
    bodyData: newBodyData,
    usedGroupingFactorIds: computeUsedGroupingFactorIds(newBodyData),
    changed: true,
  };
};
